import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { api } from "@/lib/api";
import { descriptorStyles, loadDescriptorFont } from "@/lib/descriptorFonts";

// Corner anchor styles keyed by the branding metadata `position` value.
const CORNER = {
  top_left: { top: 0, left: 0 },
  top_right: { top: 0, right: 0 },
  bottom_left: { bottom: 0, left: 0 },
  bottom_right: { bottom: 0, right: 0 },
};

// Bounded recovery budget so transient live-edge/network/codec hiccups don't permanently kill the
// embedded preview, while a persistently broken stream still surfaces an operator error.
const MAX_NETWORK_RETRIES = 4;   // fatal NETWORK_ERROR → hls.startLoad() with backoff
const MAX_MEDIA_RECOVERIES = 3;  // fatal MEDIA_ERROR → recoverMediaError() (+ swapAudioCodec on repeat)

// In-app preview player. Plays the channel HLS manifest and renders the persistent
// channel logo as a PLAYER OVERLAY using the exact same /branding metadata that
// ReVine TV clients consume — so operators see what viewers will see.
export function ChannelPreviewPlayer({ channelId, manifestUrl, programInfo }) {
  const videoRef = useRef(null);
  const [branding, setBranding] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/channels/${channelId}/branding`)
      .then((r) => setBranding(r.data.branding))
      .catch(() => {});
  }, [channelId]);

  useEffect(() => {
    if (!manifestUrl) return;
    const video = videoRef.current;
    setErr(null);

    let hls = null;
    let destroyed = false;
    let networkRetries = 0;
    let mediaRecoveries = 0;
    let recreated = false;
    let retryTimer = null;

    // ── WPCI Phase 1 — TEMPORARY diagnostic instrumentation (Web player only) ──────────────
    // OPT-IN via `?wpci=1` (never runs for normal viewers). Captures the full hls.js lifecycle,
    // MSE/SourceBuffer/video state and the COMPLETE error object, timestamped from player init,
    // so the production Web freeze (~5–10s in) can be traced. Copy `window.__wpci` in DevTools,
    // or read the auto-dump at t+30s. REMOVE after root cause is captured.
    const WPCI = typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("wpci");
    const t0 = performance.now();
    const log = [];
    if (WPCI) {
      // Create the global immediately so `window.__wpci` is defined the moment the INSTRUMENTED
      // component mounts (not only after the first hls.js event). A missing/undefined window.__wpci
      // therefore means this component/route is NOT the one rendering the player.
      window.__wpci = log;
      // eslint-disable-next-line no-console
      console.log("%c[WPCI] active — component=ChannelPreviewPlayer route=/channels/:id — trace: window.__wpci", "color:#3A86FF");
    }
    const snap = (event, extra) => {
      if (!WPCI) return;
      const t = ((performance.now() - t0) / 1000).toFixed(3);
      const lvl = hls && hls.levels ? hls.levels[hls.currentLevel] : null;
      const det = lvl && lvl.details ? lvl.details : null;
      let buffered = [];
      try {
        for (let i = 0; i < video.buffered.length; i++)
          buffered.push([+video.buffered.start(i).toFixed(3), +video.buffered.end(i).toFixed(3)]);
      } catch { /* noop */ }
      const bEnd = buffered.length ? buffered[buffered.length - 1][1] : 0;
      let q = {};
      try { q = video.getVideoPlaybackQuality ? video.getVideoPlaybackQuality() : {}; } catch { /* noop */ }
      const ms = hls && hls.media && hls.media.mediaSource ? hls.media.mediaSource : null;
      const row = {
        t, event,
        currentTime: video ? +video.currentTime.toFixed(3) : null,
        paused: video ? video.paused : null,
        readyState: video ? video.readyState : null,       // 0..4 (HAVE_ENOUGH=4)
        networkState: video ? video.networkState : null,
        buffered, bufferAhead: video ? +(bEnd - video.currentTime).toFixed(3) : null,
        liveSyncPos: hls ? hls.liveSyncPosition : null,
        latency: hls ? hls.latency : null,
        currentLevel: hls ? hls.currentLevel : null,
        bitrate: lvl ? lvl.bitrate : null,
        bwEstimate: hls ? Math.round(hls.bandwidthEstimate || 0) : null,
        mediaSeq: det ? det.startSN : null,
        endSN: det ? det.endSN : null,
        discSeq: det ? det.startCC : null,
        live: det ? det.live : null,
        targetduration: det ? det.targetduration : null,
        fragCount: det && det.fragments ? det.fragments.length : null,
        droppedFrames: q.droppedVideoFrames ?? null,
        totalFrames: q.totalVideoFrames ?? null,
        mediaSourceState: ms ? ms.readyState : null,        // closed|open|ended
        ...(extra || {}),
      };
      log.push(row);
      window.__wpci = log;
      // eslint-disable-next-line no-console
      console.log(`[WPCI ${t}s] ${event}`, row);
    };
    const wireDiagnostics = (inst) => {
      if (!WPCI) return;
      const E = Hls.Events;
      const evs = [
        "MANIFEST_LOADING", "MANIFEST_LOADED", "MANIFEST_PARSED",
        "LEVEL_LOADING", "LEVEL_LOADED", "LEVEL_UPDATED", "LEVEL_SWITCHED",
        "FRAG_LOADING", "FRAG_LOADED", "FRAG_PARSING_INIT_SEGMENT", "FRAG_PARSED",
        "FRAG_BUFFERED", "FRAG_CHANGED",
        "BUFFER_CREATED", "BUFFER_CODECS", "BUFFER_APPENDING", "BUFFER_APPENDED",
        "BUFFER_EOS", "BUFFER_FLUSHING", "BUFFER_FLUSHED",
      ];
      evs.forEach((name) => {
        if (!E[name]) return;
        inst.on(E[name], (_e, d) => {
          const extra = {};
          if (d) {
            if (d.frag) { extra.fragSN = d.frag.sn; extra.fragCC = d.frag.cc; extra.fragDur = d.frag.duration; extra.fragUrlTail = String(d.frag.url || "").slice(-40); }
            if (d.details) { extra.plStartSN = d.details.startSN; extra.plEndSN = d.details.endSN; extra.plLive = d.details.live; }
            if (d.type) extra.mimeType = d.type;
            if (d.tracks) extra.codecs = Object.keys(d.tracks).map((k) => d.tracks[k].codec).join(",");
          }
          snap(name, extra);
        });
      });
      // Native <video> element events (stalls, seeks, autoplay, visibility).
      ["playing", "waiting", "stalled", "seeking", "seeked", "ended", "pause", "canplay", "canplaythrough", "loadedmetadata", "emptied"]
        .forEach((ev) => video.addEventListener(ev, () => snap(`video:${ev}`)));
      video.addEventListener("error", () => snap("video:error", { mediaError: video.error ? { code: video.error.code, message: video.error.message } : null }));
      document.addEventListener("visibilitychange", () => snap(`visibility:${document.visibilityState}`));
      const poll = setInterval(() => { if (!destroyed) snap("tick"); }, 1000);
      const dump = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log("[WPCI] ==== 30s TIMELINE DUMP ====\n" + JSON.stringify(log, null, 2));
      }, 30000);
      inst.once(Hls.Events.DESTROYING, () => { clearInterval(poll); clearTimeout(dump); });
    };
    // ────────────────────────────────────────────────────────────────────────────────────────

    const surfaceError = (details) => { if (!destroyed) setErr(details || "playback error"); };

    // Fresh hls.js instance bound to the SAME PAS session playbackUrl — the preview flow is unchanged.
    const buildHls = () => {
      // CCS Commercial Baseline Release (CBR) v1.0 — canonical hls.js config (no playback tuning).
      // PPI live-edge overrides removed; hls.js uses its own default live sync. Bounded error
      // recovery retained (predates PPI); WPCI diagnostics remain opt-in via ?wpci=1.
      const inst = new Hls({
        liveDurationInfinity: true,
        enableWorker: true,
      });
      inst.loadSource(manifestUrl);
      inst.attachMedia(video);
      inst.on(Hls.Events.ERROR, onError);
      wireDiagnostics(inst);            // WPCI: temporary diagnostics (opt-in via ?wpci=1)
      return inst;
    };

    // Last-resort documented recovery: destroy and recreate the player ONCE before giving up.
    const recreateOnce = (details) => {
      if (recreated || destroyed) { surfaceError(details); return; }
      recreated = true;
      networkRetries = 0;
      mediaRecoveries = 0;
      try { hls && hls.destroy(); } catch { /* noop */ }
      hls = buildHls();
    };

    function onError(_evt, data) {
      // WPCI: capture the COMPLETE error object (all fields hls.js populates) at the freeze.
      if (data) {
        try {
          const full = {
            type: data.type, details: data.details, fatal: data.fatal,
            reason: data.reason, err: data.err ? String(data.err && data.err.message || data.err) : null,
            frag: data.frag ? { sn: data.frag.sn, cc: data.frag.cc, url: String(data.frag.url || "").slice(-60) } : null,
            level: data.level, url: data.url ? String(data.url).slice(-80) : null,
            response: data.response ? { code: data.response.code, text: String(data.response.text || "").slice(0, 200) } : null,
            networkDetails: data.networkDetails ? { status: data.networkDetails.status } : null,
            buffer: data.buffer, parent: data.parent,
          };
          snap("ERROR", { error: full });
        } catch { /* noop */ }
      }
      if (!data || !data.fatal || destroyed || !hls) return;
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (networkRetries < MAX_NETWORK_RETRIES) {
            networkRetries += 1;
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => { if (!destroyed && hls) hls.startLoad(); }, 500 * networkRetries);
          } else {
            recreateOnce(data.details);
          }
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          if (mediaRecoveries < MAX_MEDIA_RECOVERIES) {
            mediaRecoveries += 1;
            if (mediaRecoveries >= 2) { try { hls.swapAudioCodec(); } catch { /* noop */ } }
            hls.recoverMediaError();
          } else {
            recreateOnce(data.details);
          }
          break;
        default:
          // OTHER/MUX errors aren't fixable via start/recover — try one clean recreate, then surface.
          recreateOnce(data.details);
      }
    }

    if (Hls.isSupported()) {
      hls = buildHls();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = manifestUrl; // Safari native HLS
    } else {
      setErr("HLS not supported in this browser");
    }

    return () => {
      destroyed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (hls) { try { hls.destroy(); } catch { /* noop */ } }
    };
  }, [manifestUrl]);

  // Render from the v2 Graphics Elements collection (Channel Graphics Framework). Each element
  // shares one envelope {type, enabled, layer, geometry, opacity, payload}; Phase 1 handles the
  // "logo" type only. Falls back to the retained v1 `logo` object so a v1 payload still renders
  // identically. Elements are drawn in ascending `layer` order (back → front).
  const legacyLogo = branding?.logo;
  const elements = Array.isArray(branding?.graphics) && branding.graphics.length
    ? branding.graphics
    : (legacyLogo
        ? [{
            id: "logo-primary", type: "logo", enabled: legacyLogo.visible, layer: 0,
            geometry: { anchor: legacyLogo.position, width_pct: legacyLogo.width_pct,
              margin_pct: branding?.safe_area?.margin_pct ?? 5 },
            opacity: legacyLogo.opacity, payload: { url: legacyLogo.url },
          }]
        : []);
  const graphics = [...elements]
    .filter((el) => el && el.type === "logo" && el.enabled && el.payload?.url)
    .sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0));

  // Channel-wide descriptor appearance (font / size / text-background) — the SAME config GRS burns
  // in, so the preview matches the rendered output. Load the selected family for the overlay.
  const descriptor = branding?.descriptor;
  useEffect(() => { if (descriptor?.css_family) loadDescriptorFont(descriptor.css_family); }, [descriptor?.css_family]);
  const ds = descriptorStyles(descriptor);

  return (
    <div className="relative w-full aspect-video bg-black rounded-sm overflow-hidden border border-[#262626]" data-testid="channel-preview-player">
      <video ref={videoRef} data-testid="preview-video" className="w-full h-full object-contain bg-black" muted autoPlay playsInline controls />
      {graphics.map((el, i) => (
        <div key={el.id || i} className="absolute pointer-events-none"
          data-testid={i === 0 ? "brand-overlay" : `graphic-${el.type}-${i}`}
          style={{ ...(CORNER[el.geometry?.anchor] || CORNER.top_right),
            margin: `${el.geometry?.margin_pct ?? 5}%`,
            width: `${el.geometry?.width_pct ?? 12}%`, opacity: el.opacity, zIndex: el.layer ?? 0 }}>
          <img src={el.payload.url} alt={`channel ${el.type}`} className="w-full object-contain" />
        </div>
      ))}
      {err && (
        <div className="absolute bottom-2 left-2 text-[10px] font-mono text-amber-400 bg-black/70 px-2 py-1 rounded-sm" data-testid="preview-error">
          Preview unavailable here: {err} — open the manifest in a browser/VLC.
        </div>
      )}
      {programInfo?.enabled && (programInfo.title || programInfo.label || programInfo.description) && (
        <div className="absolute pointer-events-none max-w-[45%]"
          data-testid="program-info-overlay"
          style={{ ...(CORNER[programInfo.position] || CORNER.bottom_left), margin: "5%", zIndex: 5 }}>
          <div className="rounded-sm px-3 py-2 backdrop-blur-[2px]" style={{ ...ds.box, ...ds.text }}>
            {programInfo.label && (
              <div className="text-white/80 font-semibold tracking-wider uppercase leading-none" style={{ fontSize: ds.labelPx }} data-testid="program-info-label">{programInfo.label}</div>
            )}
            {programInfo.title && (
              <div className="text-white font-bold leading-tight mt-0.5" style={{ fontSize: ds.titlePx }} data-testid="program-info-title">{programInfo.title}</div>
            )}
            {programInfo.description && (
              <div className="text-white/75 leading-snug mt-0.5" style={{ fontSize: ds.descPx }} data-testid="program-info-description">{programInfo.description}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
